<template>
    <div class="congress-schedule-event-list">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state">
            <p class="mb-4">
                {{ lang(`congresses::congresses.schedule.empty_${eventType}`) }}
            </p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addEvent">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/list-plus"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.schedule.add_event') }}
                </span>
            </Button>
        </div>

        <!-- Events List (Sortable) -->
        <div v-else>
            <Sortable :list="modelValue" item-key="_id" tag="div" @end="onEventsReordered">
                <template #item="{ element: event, index }">
                    <div :key="`${event._id}-${index}`" class="tree-line-item grid grid-cols-12">
                        <div class="col-span-1">
                            <TreeLine />
                        </div>
                        <div class="col-span-11">
                            <ScheduleEventCard
                                :key="event._id"
                                v-model="modelValue[index]"
                                :event-type="eventType"
                                :day-index="dayIndex"
                                :event-index="index"
                                :available-tickets="availableTickets"
                                @remove="removeEvent(index)"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>

            <!-- Add Event Button -->
            <div class="congress-schedule-after-event-list-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addEvent">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/list-plus"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.schedule.add_event') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import TreeLine from '@src/Common/Components/TreeLine.vue';
import ScheduleEventCard from './ScheduleEventCard.vue';
import type { ScheduleEvent } from '../types';
import type { Ticket } from '../types';

const props = defineProps<{
    eventType: 'schedule' | 'program';
    dayIndex: number;
    availableTickets?: Ticket[];
}>();

const modelValue = defineModel<ScheduleEvent[]>();

const addEvent = () => {
    if (!modelValue.value) {
        modelValue.value = [];
    }

    const baseEvent = {
        _id: uuidv4(),
        type: props.eventType,
        title: '',
        sort_order: modelValue.value.length,
    };

    if (props.eventType === 'schedule') {
        modelValue.value = [
            ...modelValue.value,
            {
                ...baseEvent,
                time: '',
                location: '',
                speakers: [],
            } as ScheduleEvent,
        ];
    } else {
        modelValue.value = [
            ...modelValue.value,
            {
                ...baseEvent,
                description: '',
                schedule: [],
                price: '',
                currency: 'CZK',
                date_display: '',
                time_display: '',
                location: '',
            } as ScheduleEvent,
        ];
    }
};

const removeEvent = (index: number) => {
    if (confirm(lang('congresses::congresses.schedule.confirm_remove_event'))) {
        modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
    }
};

const onEventsReordered = (event: { oldIndex: number; newIndex: number }) => {
    // Manually reorder the array based on drag indices
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    // Update sort_order for all items
    items.forEach((item, idx) => {
        item.sort_order = idx;
    });

    // Assign new array to trigger reactivity
    modelValue.value = items;
};
</script>
